import { z } from 'zod';
import { montarCaminho, requisitar } from './client.js';
import type { Pagina, ParametrosDePagina } from './paginacao.js';
import { paginaSchema, parametrosDePagina } from './paginacao.js';
import type { components } from './schema.js';
import { analisar } from './validacao.js';

/**
 * Divergências de saldo (r1-sync).
 *
 * A sincronização lê o saldo do ERP por filial + depósito + produto. Saldo que
 * já existia aqui e veio DIFERENTE nunca é sobrescrito sozinho: nasce uma
 * divergência pendente, e alguém do painel decide — `Atualizar` aplica a
 * leitura do ERP, `Manter` a descarta. A decisão vai para a trilha de auditoria
 * (`saldo.divergencia_decidida`) e não se desfaz.
 *
 * Quantidades vêm como texto na escala do banco (`10.000000`): a tela nunca as
 * passa por `Number` (ver `pages/movimentacoes/quantidade.ts`).
 */

type Esquemas = components['schemas'];

const CAMINHO_COLECAO = montarCaminho('/saldos/divergencias');
const CAMINHO_CONTADOR = montarCaminho('/saldos/divergencias/contador');

export const statusDivergenciaSaldoSchema = z.enum(['Pendente', 'Atualizada', 'Mantida']);

export type StatusDivergenciaSaldo = z.infer<typeof statusDivergenciaSaldoSchema>;

/**
 * As quantidades comparadas. Chave ausente: não comparada; `null`: o ERP não
 * informa. O objeto inteiro é `null` quando o valor gravado saiu do contrato —
 * a tela mostra o que tem e não inventa o resto.
 */
const valoresDoSaldoSchema = z.record(z.string(), z.string().nullable()).nullable();

export type ValoresDoSaldo = z.infer<typeof valoresDoSaldoSchema>;

/** Conferido contra o OpenAPI em tempo de compilação. */
const divergenciaDeSaldoSchema: z.ZodType<Esquemas['DivergenciaDeSaldoResponse']> = z.object({
  id: z.string(),
  status: statusDivergenciaSaldoSchema,
  saldoId: z.string(),
  codEmp: z.number().int(),
  codFil: z.number().int(),
  codDep: z.string(),
  codPro: z.string(),
  /** Vazio quando o produto não tem derivação. */
  codDer: z.string(),
  /** Vazio quando o saldo não é por lote. */
  codLot: z.string(),
  /** Vazio quando o saldo não é por série. */
  numSep: z.string(),
  produto: z.object({
    nome: z.string(),
    descricaoDerivacao: z.string().nullable(),
    unidadeMedida: z.string(),
  }),
  /** O que estava gravado aqui quando a divergência nasceu. */
  quantidadeDisponivelLocal: z.string(),
  /** A leitura do ERP — o que `Atualizar` aplica. */
  quantidadeDisponivelErp: z.string(),
  /** ERP menos local, com sinal. */
  diferenca: z.string(),
  /** A disponível do saldo agora (depois da decisão, quando decidida). */
  quantidadeDisponivelAtual: z.string(),
  /** Falso quando o saldo foi excluído no ERP. */
  saldoAtivo: z.boolean(),
  valoresLocal: valoresDoSaldoSchema,
  valoresErp: valoresDoSaldoSchema,
  detectadaEm: z.string(),
  /** Instante da leitura do ERP que está em `valoresErp`. */
  sincronizadoEm: z.string(),
  /** Login de quem decidiu. */
  decididoPor: z.string().nullable(),
  decididoEm: z.string().nullable(),
  justificativa: z.string().nullable(),
});

export type DivergenciaDeSaldo = z.infer<typeof divergenciaDeSaldoSchema>;

const paginaDeDivergenciasSchema = paginaSchema(divergenciaDeSaldoSchema);

const contadorSchema: z.ZodType<Esquemas['ContadorDeDivergenciasDeSaldoResponse']> = z.object({
  pendentes: z.number().int().nonnegative(),
});

export type FiltroDeDivergenciasDeSaldo = ParametrosDePagina & {
  readonly status?: StatusDivergenciaSaldo;
  /** `codPro` exato. */
  readonly produto?: string;
  /** `codDer` exato. */
  readonly derivacao?: string;
  /** Código do depósito (caixa não importa). */
  readonly deposito?: string;
  /** Com `deposito`, restringe o par; sozinha, a filial inteira. */
  readonly codFil?: number;
};

/** Da mais antiga para a mais nova: a que espera há mais tempo vem primeiro. */
export async function listarDivergenciasDeSaldo(
  filtro: FiltroDeDivergenciasDeSaldo = {},
): Promise<Pagina<DivergenciaDeSaldo>> {
  const corpo = await requisitar(CAMINHO_COLECAO, {
    consulta: {
      ...parametrosDePagina(filtro),
      status: filtro.status,
      produto: filtro.produto,
      derivacao: filtro.derivacao,
      deposito: filtro.deposito,
      codFil: filtro.codFil,
    },
  });

  return analisar(paginaDeDivergenciasSchema, corpo, CAMINHO_COLECAO);
}

/** Quantas esperam decisão — a notificação da barra lateral. */
export async function contarDivergenciasDeSaldoPendentes(): Promise<number> {
  const corpo = await requisitar(CAMINHO_CONTADOR);
  return analisar(contadorSchema, corpo, CAMINHO_CONTADOR).pendentes;
}

export type DecisaoDaDivergenciaDeSaldo = Esquemas['DecidirDivergenciaSaldoRequest']['decisao'];

export type PedidoDeDecisaoDeSaldo = {
  readonly decisao: DecisaoDaDivergenciaDeSaldo;
  /** Opcional, até 500 caracteres. Em branco não vai. */
  readonly justificativa?: string;
};

/**
 * Decide a divergência (Administrador e Supervisor — os mesmos perfis da
 * divergência da movimentação em duas etapas).
 *
 * Não é idempotente de propósito: a segunda decisão da mesma divergência volta
 * `DIVERGENCIA_SALDO_JA_DECIDIDA` (409).
 */
export async function decidirDivergenciaDeSaldo(
  id: string,
  pedido: PedidoDeDecisaoDeSaldo,
): Promise<DivergenciaDeSaldo> {
  const caminho = montarCaminho('/saldos/divergencias/{id}/decidir', { id });
  const justificativa = pedido.justificativa?.trim() ?? '';
  const corpo = await requisitar(caminho, {
    metodo: 'POST',
    corpo: {
      decisao: pedido.decisao,
      ...(justificativa === '' ? {} : { justificativa }),
    },
  });

  return analisar(divergenciaDeSaldoSchema, corpo, caminho);
}
